import React, { useState } from 'react';
import styled from 'styled-components';

import Modal from '../common/Modal';
import {
  OptionItem as OptionItemBase,
  OptionList as OptionListBase,
} from '../common/OptionList';

type PhotoHuntSource = 'story' | 'org' | 'stock';

const SOURCE_OPTIONS: { text: string; value: PhotoHuntSource }[] = [
  {
    text: 'Story',
    value: 'story',
  },
  {
    text: 'Organizational',
    value: 'org',
  },
  {
    text: 'Stock',
    value: 'stock',
  },
];

export default function FeelingLuckyModal({
  closeModal,
  renderActions,
}: {
  closeModal: () => void;
  renderActions: (args: {
    sources: PhotoHuntSource[];
    disabled: boolean;
  }) => React.ReactNode;
}) {
  const [sources, setSources] = useState<PhotoHuntSource[]>([]);

  const toggleSource = (value: PhotoHuntSource) => {
    if (sources.includes(value)) {
      setSources(sources.filter(s => s !== value));
    } else {
      setSources([...sources, value]);
    }
  };

  return (
    <Modal isOpen closeModal={closeModal}>
      <Wrapper>
        <Content>
          <Header>
            <ModalName>Feeling lucky</ModalName>
            <Title>Select photos</Title>
            <Hint>Select photo sources you’d like to use</Hint>
          </Header>
          <OptionList>
            {SOURCE_OPTIONS.map(o => (
              <OptionItem
                isCurrent={sources.includes(o.value)}
                onClick={() => toggleSource(o.value)}
                key={o.value}
              >
                {o.text}
              </OptionItem>
            ))}
          </OptionList>
          <ActionsWrapper>
            {renderActions({ sources, disabled: sources.length === 0 })}
          </ActionsWrapper>
        </Content>
      </Wrapper>
    </Modal>
  );
}

const Wrapper = styled.div`
  display: inline-flex;
  padding: var(--spacing-2xl) var(--spacing-lg);
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: var(--spacing-2xl);
  border-radius: 16px;
  border: 1px solid var(--border);
  background: var(--background);
  /* Drop Shadow */
  box-shadow: var(--elevation-toast);
`;

const Content = styled.div`
  display: flex;
  width: 440px;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-lg);
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-sm);
  width: 100%;
`;

const ModalName = styled.h1`
  margin: 0;
  color: var(--text-muted);
  text-align: center;
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
`;

const Title = styled.h2`
  align-self: stretch;
  color: var(--foreground);
  text-align: center;
  font-family: var(--font-primary);
  font-size: 32px;
  font-style: normal;
  font-weight: var(--font-weight-bold);
  line-height: 120%; /* 38.4px */
  margin: 0;
`;

const Hint = styled.h3`
  align-self: stretch;
  color: var(--text-secondary);
  text-align: center;
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-style: normal;
  font-weight: var(--font-weight-normal);
  line-height: 140%; /* 16.8px */
  margin: 0;
`;

const OptionList = styled(OptionListBase)`
  flex-wrap: wrap;
  width: 100%;
`;

const OptionItem = styled(OptionItemBase)`
  min-width: 120px;
`;

const ActionsWrapper = styled.div`
  display: flex;
  gap: var(--spacing-sm);
  width: 100%;
`;

export const ProceedButton = styled.button.attrs(
  (props: { disabled?: boolean }) => props,
)`
  flex: 1;
  display: flex;
  height: 48px;
  padding: var(--spacing-md);
  justify-content: center;
  align-items: center;
  gap: var(--spacing-xs);
  align-self: stretch;
  border: none;
  border-radius: var(--radius);
  background: ${props =>
    props.disabled ? 'var(--text-muted)' : 'var(--primary)'};
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  color: var(--background);
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-style: normal;
  font-weight: var(--font-weight-bold);
  line-height: normal;
`;
